import { GQL } from '@aline/graphql';
import { equals } from 'ramda';
import type { Location, NodeEntry, Range } from 'slate';
import { Editor, Node, Path, Transforms } from 'slate';

import type { UUID } from '../../types/type-helpers';
import { mergeWithDefaults } from '../../util';
import type {
  AlineContentBlock,
  AlineText,
  DynamicContentElement,
  DynamicContentProps,
  DynamicContentType,
  EditingMode,
  ProcessTemplateField,
  TextProps,
  Trigger,
  TriggerCondition,
} from '../internal';
import {
  AlineTransforms,
  createDefaultParagraph,
  generateLocationId,
  indexFieldsByNodeRefId,
  isDynamicContentElement,
  Queries,
} from '../internal';

export interface RequireSomeDynamicContentProps extends Partial<DynamicContentProps> {
  fieldId: UUID;
  type: DynamicContentType;
  variant: GQL.FieldVariant.Dropdown | GQL.FieldVariant.Radiobutton;
}

interface AnswerOptions extends Pick<GQL.AnswerOptions, '__typename'> {
  options: Pick<GQL.AnswerOption, '__typename' | 'choiceRefId'>[];
}
export interface RemoveUnreachableTriggerVariable extends Pick<
  GQL.DocumentVariable,
  'nodeId' | '__typename' | 'variant'
> {
  answerOptions: AnswerOptions;
}

const DynamicContentTransforms = {
  /**
   * Inserts an input DynamicContent into the document.
   */
  insertDynamicContent: (
    editor: Editor,
    options: {
      authorId: UUID;
      dynamicContentProps: RequireSomeDynamicContentProps;
      editingMode: EditingMode;
      at?: Range;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        dynamicContentProps,
        editingMode,
        select = true,
      } = options;
      if (!at) return;

      const resolvedRange = AlineTransforms.nudgeRangeToEdges(editor, { at });
      const textProps = Queries.getFallbackTextProps(editor, { at: resolvedRange, editingMode });
      const dynamicContent = createDynamicContentElement({
        dynamicContentProps,
        textProps,
      });

      // Have to do weird switch to satisfy TS on the type of nodes for AlineTransforms.insertContentNodes.
      switch (dynamicContent.type) {
        case 'dynamic-block': {
          AlineTransforms.insertContentNodes(editor, {
            at: resolvedRange,
            authorId,
            editingMode,
            nodes: [dynamicContent],
            select: select ? 'start' : 'none',
            voids: true,
          });
          break;
        }
        case 'dynamic-inline': {
          AlineTransforms.insertContentNodes(editor, {
            at: resolvedRange,
            authorId,
            editingMode,
            nodes: [dynamicContent],
            select: select ? 'start' : 'none',
            voids: true,
          });
          break;
        }
      }
    });
  },

  /**
   * Removes "unreachable" triggers from DynamicContentElements.  An unreachable trigger is a trigger that has
   * a condition that points to a non-existent fieldId or non-existent AnswerOption.choiceRefId.
   */
  removeUnreachableTriggers: (
    editor: Editor,
    options: { inputFields: RemoveUnreachableTriggerVariable[]; fieldId?: UUID }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { fieldId, inputFields } = options;
      const allFieldIds = new Set<UUID>();
      const allChoiceRefIds = new Set<UUID>();
      inputFields.forEach(inputField => {
        allFieldIds.add(inputField.nodeId);
        inputField.answerOptions.options.forEach(o => allChoiceRefIds.add(o.choiceRefId));
      });

      const dynamicContentEntries = Editor.nodes<DynamicContentElement>(editor, {
        at: [],
        match: n => {
          if (!fieldId) return isDynamicContentElement(n);

          return isDynamicContentElement(n) && n.fieldId === fieldId;
        },
        voids: true,
      });

      for (const [dCNode, dCPath] of dynamicContentEntries) {
        const { initialTriggerIndex, triggerIndex, triggers = [] } = dCNode;
        const removedIndices = new Set<number>();
        const reachableTriggers = triggers.filter((trigger, index) => {
          // Trigger is unreachable if any condition doesn't have a matching fieldId or choiceRefId.
          const isUnreachable = trigger.conditions.some(
            condition =>
              !allFieldIds.has(condition.fieldId) ||
              !allChoiceRefIds.has(condition.answerOptionChoiceId)
          );
          if (isUnreachable) {
            removedIndices.add(index);
            return false;
          }

          return true;
        });
        if (reachableTriggers.length === triggers.length) continue;

        const newTriggerIndex =
          typeof triggerIndex === 'number' && removedIndices.has(triggerIndex)
            ? 'not-set'
            : triggerIndex;
        const newInitTriggerIndex =
          typeof initialTriggerIndex === 'number' && removedIndices.has(initialTriggerIndex)
            ? 'not-set'
            : initialTriggerIndex;

        AlineTransforms.setDynamicContentProps(editor, {
          at: dCPath,
          props: {
            initialTriggerIndex: newInitTriggerIndex,
            triggerIndex: newTriggerIndex,
            triggers: reachableTriggers,
          },
        });
      }
    });
  },

  /**
   * Reset a DynamicContentNode to its "default" - i.e., the contentJSON corresponding to initialTriggerIndex.
   */
  resetDynamicContentNode(
    editor: Editor,
    options: {
      assigneeId: UUID | null;
      authorId: UUID;
      dynamicContentEntry: NodeEntry<DynamicContentElement>;
      editingMode: EditingMode;
      fields: ProcessTemplateField[];
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { assigneeId, authorId, dynamicContentEntry, editingMode, fields } = options;
      const [dynamicContent, dynamicContentPath] = dynamicContentEntry;
      const { initialTriggerIndex, triggers = [] } = dynamicContent;
      const fieldsIndexedByNodeRefId = indexFieldsByNodeRefId(fields);
      const initializedTriggers = triggers.map(trigger => ({
        ...trigger,
        contentJSON: Queries.getInitializedTriggerJSON({
          dynamicContentNode: { children: trigger.contentJSON },
          fields: fieldsIndexedByNodeRefId,
        }),
      }));
      const initialTrigger =
        typeof initialTriggerIndex === 'number'
          ? (initializedTriggers[initialTriggerIndex] ?? 'not-set')
          : 'not-set';

      // Set triggers: initializedTriggers *first* so that switchDynamicContentTrigger switches to the correct "initialized" trigger.
      AlineTransforms.setDynamicContentProps(editor, {
        at: dynamicContentPath,
        props: { triggers: initializedTriggers },
      });
      AlineTransforms.switchDynamicContentTrigger(editor, {
        assigneeId,
        authorId,
        dynamicContentPath,
        editingMode,
        selectedTrigger: initialTrigger,
      });
    });
  },

  /**
   * Helper func to provide type-safety for setting DC props.
   */
  setDynamicContentProps(
    editor: Editor,
    options: { props: Partial<DynamicContentProps>; at?: Location }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, props } = options;
      if (!at) return;

      const matchPath = Path.isPath(at) ? Queries.getMatchPath(editor, at) : null;

      Transforms.setNodes<DynamicContentElement>(editor, props, {
        at,
        // hanging: true so Slate doesn't modify at internally and try and "unhang" it.
        hanging: true,
        match: n => {
          const isDynamicContent = isDynamicContentElement(n);
          if (!matchPath) return isDynamicContent;

          return isDynamicContent && matchPath(n);
        },
        // Use mode: 'all' vs. the default of mode: 'lowest' to match all DCs at the provided location.
        mode: 'all',
        voids: true,
      });
    });
  },

  /**
   * Helper to switch all DynamicContentElements with a fieldId to a selectedTrigger.
   */
  switchAllDCsWithFieldId(
    editor: Editor,
    options: {
      currentUserId: UUID;
      editingMode: EditingMode;
      fieldId: UUID;
      selectedTrigger: Omit<TriggerCondition, 'option-choice'> | 'not-set';
      assigneeId?: UUID | null;
      select?: boolean;
    }
  ): { updated: boolean; errMessage?: string } {
    return AlineTransforms.withoutNormalizing<{ updated: boolean; errMessage?: string }>(
      editor,
      () => {
        const {
          assigneeId,
          currentUserId,
          editingMode,
          fieldId,
          select = true,
          selectedTrigger,
        } = options;
        const dynamicContentEntries = [
          ...Editor.nodes<DynamicContentElement>(editor, {
            at: [],
            match: n => isDynamicContentElement(n) && n.fieldId === fieldId,
            voids: true,
          }),
        ];
        const isInSigningCeremony = Boolean(editor.userState?.isInSigningCeremony);
        const isInvalidSwitch =
          isInSigningCeremony &&
          dynamicContentEntries.some(([node]) => Queries.dynamicElementHasSignatures(node));
        if (isInvalidSwitch)
          return {
            errMessage:
              'The dynamic block has signatures and cannot be updated during signing. Revert workflow to negotiating to update dynamic block.',
            updated: false,
          };

        for (const [, dynamicContentPath] of dynamicContentEntries) {
          AlineTransforms.switchDynamicContentTrigger(editor, {
            assigneeId: assigneeId ?? null,
            authorId: currentUserId,
            dynamicContentPath,
            editingMode,
            select,
            selectedTrigger:
              selectedTrigger === 'not-set' ? 'not-set' : { conditions: [selectedTrigger] },
          });
        }

        return { updated: dynamicContentEntries.length > 0 };
      }
    );
  },

  /**
   * Removes DynamicContentElement's children and replaces with newChildren.  Useful when switching triggers, etc.
   * Perf - use editor.apply directly so switching large DCs is not slow.
   */
  switchDynamicContentChildren(
    editor: Editor,
    options: {
      entry: NodeEntry<DynamicContentElement>;
      newChildren: AlineContentBlock[] | AlineText[];
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { entry, newChildren } = options;
      const [dynamicContentNode, dynamicContentPath] = entry;

      // Remove all DC children.  Do in reverse so we don't have to worry about paths changing under our feet.
      for (let i = dynamicContentNode.children.length - 1; i >= 0; i--) {
        const path = [...dynamicContentPath, i];
        const node = Node.get(editor, path);

        editor.apply({ node, path: [...dynamicContentPath, i], type: 'remove_node' });
      }

      // Insert new children.
      for (let i = 0; i < newChildren.length; i++) {
        const path = [...dynamicContentPath, i];
        const node = newChildren[i];

        editor.apply({ node, path, type: 'insert_node' });
      }
    });
  },

  /**
   * Sets the triggerIndex (and upserts the trigger) for a DynamicContent,
   * given an option field change.
   */
  switchDynamicContentTrigger: (
    editor: Editor,
    options: {
      assigneeId: UUID | null;
      authorId: UUID;
      dynamicContentPath: Path;
      editingMode: EditingMode;
      selectedTrigger: Omit<Trigger, 'linkOperator' | 'contentJSON'> | 'not-set';
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        assigneeId,
        dynamicContentPath,
        editingMode,
        select = true,
        selectedTrigger,
      } = options;
      const dynamicContentNode = Node.get(editor, dynamicContentPath);
      const { isUserAuthorized } = Queries.isUserAuthorizedToCompleteNonSigField(editor, {
        assigneeId,
      });
      if (!isDynamicContentElement(dynamicContentNode) || !isUserAuthorized) return;

      const isInSigningCeremony = Boolean(editor.userState?.isInSigningCeremony);
      const isInvalidSwitch =
        isInSigningCeremony && Queries.dynamicElementHasSignatures(dynamicContentNode);
      if (isInvalidSwitch) return;

      const {
        trigger: newTrigger,
        triggerIndex,
        triggers,
      } = findOrCreateMatchingTrigger(selectedTrigger, dynamicContentNode);

      // Update DC's triggers.
      AlineTransforms.setDynamicContentProps(editor, {
        at: dynamicContentPath,
        props: { triggerIndex, triggers },
      });

      // Match DC's children to the new trigger's content.
      const textProps = Queries.getFallbackTextProps(editor, {
        at: dynamicContentPath,
        editingMode,
      });
      const newChildren = Queries.getTriggerChildren(newTrigger, { textProps });

      AlineTransforms.switchDynamicContentChildren(editor, {
        entry: [dynamicContentNode, dynamicContentPath],
        newChildren,
      });

      if (select) AlineTransforms.select(editor, Editor.start(editor, dynamicContentPath));
    });
  },

  /**
   * Sets initialTriggerIndex or the "default" for a DynamicContent element by it's fieldId.
   */
  upsertDynamicContentDefault: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      fieldId: UUID;
      initialTrigger: Omit<Trigger, 'linkOperator' | 'contentJSON'> | 'not-set';
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { fieldId, initialTrigger } = options;
      const dynamicContentEntries = Editor.nodes<DynamicContentElement>(editor, {
        at: [],
        match: n => isDynamicContentElement(n) && n.fieldId === fieldId,
        voids: true,
      });

      for (const [dCNode, dCPath] of dynamicContentEntries) {
        const { initialTriggerIndex, triggers = [] } = dCNode;
        if (initialTrigger === 'not-set') {
          AlineTransforms.setDynamicContentProps(editor, {
            at: dCPath,
            props: { initialTriggerIndex: 'not-set' },
          });
          continue;
        }

        const existingInitTriggerIndex = triggers.findIndex(existingTrigger =>
          equals(existingTrigger.conditions, initialTrigger.conditions)
        );
        if (existingInitTriggerIndex === -1) {
          const newTrigger: Trigger = { contentJSON: [], linkOperator: 'and', ...initialTrigger };
          const newTriggers = [...triggers, newTrigger];

          AlineTransforms.setDynamicContentProps(editor, {
            at: dCPath,
            props: { initialTriggerIndex: triggers.length, triggers: newTriggers },
          });
          continue;
        }

        // The trigger already exists.  Make sure initialTriggerIndex is set correctly.
        if (existingInitTriggerIndex === initialTriggerIndex) continue;

        AlineTransforms.setDynamicContentProps(editor, {
          at: dCPath,
          props: { initialTriggerIndex: existingInitTriggerIndex },
        });
      }
    });
  },
};

const defaultDynamicContentProps: Omit<DynamicContentProps, 'fieldId' | 'type'> = {
  initialTriggerIndex: 'not-set',
  suggestedEdits: [],
  triggerIndex: 'not-set',
  triggers: [],
  variant: GQL.FieldVariant.Dropdown,
};

interface CreateDynamicContentElement {
  dynamicContentProps: RequireSomeDynamicContentProps;
  text?: string;
  textProps?: TextProps;
}

export function createDynamicContentElement({
  dynamicContentProps,
  text = '',
  textProps,
}: CreateDynamicContentElement): DynamicContentElement {
  return {
    ...mergeWithDefaults(dynamicContentProps, defaultDynamicContentProps),
    children: [createDefaultParagraph({ text, textProps })],
    locationId: generateLocationId(),
    type: 'dynamic-block',
  };
}

/**
 * Upserts provided trigger to the trigger array.
 * Also returns the updated triggerIndex to point to the new/existing trigger.
 */
function findOrCreateMatchingTrigger(
  newTrigger: Pick<Trigger, 'conditions'> | 'not-set',
  dynamicContentNode: DynamicContentElement
): {
  trigger: Trigger | 'not-set';
  triggerIndex: number | 'not-set';
  triggers: Trigger[];
  triggersOrIndexUpdated: boolean;
} {
  const { triggerIndex, triggers = [] } = dynamicContentNode;
  if (newTrigger === 'not-set') {
    return {
      trigger: 'not-set',
      triggerIndex: 'not-set',
      triggers,
      triggersOrIndexUpdated: triggerIndex !== 'not-set',
    };
  }
  const foundTriggerIndex = triggers.findIndex(existingTrigger => {
    return equals(existingTrigger.conditions, newTrigger.conditions);
  });

  const shouldAddTrigger = foundTriggerIndex === -1;
  if (shouldAddTrigger) {
    const newTriggers: Trigger[] = [
      ...triggers,
      { contentJSON: [], linkOperator: 'and', ...newTrigger },
    ];
    const newTriggerIndex = newTriggers.length - 1;
    const trigger = newTriggers[newTriggerIndex];
    return {
      trigger,
      triggerIndex: newTriggerIndex,
      triggers: newTriggers,
      triggersOrIndexUpdated: true,
    };
  }

  return {
    trigger: triggers[foundTriggerIndex],
    triggerIndex: foundTriggerIndex,
    triggers,
    triggersOrIndexUpdated: foundTriggerIndex !== triggerIndex,
  };
}

export default DynamicContentTransforms;
